import { defineStore } from 'pinia';
import {Question} from '@/courses/module/questions/QuestionModel';
import {DateTime} from 'luxon';
import {alertController} from '@ionic/vue';
import {
    getCourseModuleQuery,
    getTodaysQuestions,
    getUnlockedCourseModulesQuery
} from '@/courses/module/questions/QuestionService';
import {formatAsIsoDate} from '@/framework/formatting/DateFormatting';
import {shuffle} from '@/utils/utils';
import useAppLanguageStore from '@/settings/languages/AppLanguageStore';

interface QuestionData {
    unansweredQuestions: Question[];
    dateRetrieved: string;
    nextDeliveryDate: string;
    selectedLanguageId: string;
}

interface ModuleQuestions {
    [index: string]: QuestionData;
}

const useQuestionStore = defineStore('questionStore', {
    state: () => {
        const millisecondsInOneDay = 86400000;
        // Default to yesterday to force loading the questions from the API if we haven't yet
        const yesterday = DateTime.now().minus(millisecondsInOneDay);
        const dateRetrieved = formatAsIsoDate(yesterday);
        const unansweredQuestionKeys = Object.entries(localStorage)
            .flatMap(entry => entry[0])
            .filter(entry => entry.includes('unansweredQuestions'));
        const moduleIds = unansweredQuestionKeys.map(q => q.replace('unansweredQuestions-', ''));
        const data = {} as ModuleQuestions;
        for (const moduleId of moduleIds) {
            const questions = localStorage.getItem('unansweredQuestions-' + moduleId);            
            const date = localStorage.getItem('dateRetrieved-' + moduleId);
            const nextDeliveryDate = localStorage.getItem('nextDeliveryDate-' + moduleId) ?? '';
            const selectedLanguageId = localStorage.getItem('selectedLanguageId-' + moduleId) ?? '';
            data[moduleId] = data[moduleId] ?? {
                unansweredQuestions: questions ? JSON.parse(questions) : [],
                dateRetrieved: date ?? dateRetrieved,
                nextDeliveryDate,
                selectedLanguageId
            };
        }

        return data;
    },
    actions: {
        async showNoMoreQuestions(moduleId: string) {
            const unlockInfo = await getUnlockedCourseModulesQuery(moduleId);
            let message = '';
            if(unlockInfo.isComplete){
                message = useAppLanguageStore().language.questions.complete;
                if (unlockInfo.unlockedModuleNames?.length > 0) {
                    message = useAppLanguageStore().language.questions.congratulationsCompletionMessage;
                }
                await this.loadQuestions(moduleId);
            }
            else{
                message = useAppLanguageStore().language.questions.completeForToday.replace('{{ nextDeliveryDate }}', this[moduleId].nextDeliveryDate);
            }

            // criteria on TAR-1023 - once questions completed for the day for a module, default back to preferred language
            localStorage.removeItem('selectedLanguageId-' + moduleId);
            this[moduleId].selectedLanguageId = '';
            
            const alert = await alertController.create({
                message: message,
                buttons: [useAppLanguageStore().language.common.ok.toUpperCase()]
            });
            await alert.present();
        },
        async loadQuestions(moduleId: string) {
            // if a module (course_module) is not active a 403 is returned
            // from the api in the get questions for today validator
            // only get questions for active modules
            const courseModule = await getCourseModuleQuery(moduleId);
            if (!courseModule || !courseModule.isActive) {
                return;
            }
            const nowString = formatAsIsoDate(DateTime.now());
            this[moduleId] = this[moduleId] ?? {
                unansweredQuestions: [],
                dateRetrieved: '',
                nextDeliveryDate: '',
                selectedLanguageId: ''
            };
            let unansweredQuestions = this[moduleId].unansweredQuestions;
            
            const response = await getTodaysQuestions(moduleId);
            const questions = response.questions;
            for (const question of questions) {
                if (question.questionOption.randomizeOptionOrder) {
                    question.questionOption.responseOptions = shuffle(question.questionOption.responseOptions);
                }
                question.deliveredOn = nowString;
            }
            unansweredQuestions = questions;

            localStorage.setItem('unansweredQuestions-' + moduleId, JSON.stringify(questions));
            localStorage.setItem('dateRetrieved-' + moduleId, nowString);
            localStorage.setItem('nextDeliveryDate-' + moduleId, response.nextDeliveryDate);
            this[moduleId].unansweredQuestions = questions;
            this[moduleId].dateRetrieved = nowString;
            this[moduleId].nextDeliveryDate = response.nextDeliveryDate;

            if (!unansweredQuestions.length) {
                localStorage.setItem('unansweredQuestions-' + moduleId, JSON.stringify([]));
            }
        },
        async answerQuestion(questionId: string, moduleId: string) {
            localStorage.setItem('answeredQuestions-' + moduleId, formatAsIsoDate(DateTime.now()));
            const moduleQuestions = this[moduleId].unansweredQuestions.filter((q: Question) => q.questionOption.questionId !== questionId);
            this[moduleId].unansweredQuestions = moduleQuestions;
            await this.loadQuestions(moduleId);
            if (moduleQuestions.length) {
                localStorage.setItem('unansweredQuestions-' + moduleId, JSON.stringify(moduleQuestions));
            }
        },
        resetModule(moduleId: string) {
            localStorage.setItem('unansweredQuestions-' + moduleId, '[]');
            localStorage.setItem('dateRetrieved-' + moduleId, '');
            localStorage.setItem('nextDeliveryDate-' + moduleId, '');
            localStorage.removeItem('selectedLanguageId-' + moduleId);
            this[moduleId] = {
                unansweredQuestions: [],
                dateRetrieved: '',
                nextDeliveryDate: '',
                selectedLanguageId: ''
            };
        },
        async setSelectedLanguage(moduleId: string, languageId: string) {
            localStorage.setItem('selectedLanguageId-' + moduleId, languageId);
            this[moduleId].selectedLanguageId = languageId;
            await this.loadQuestions(moduleId);
        }
    },
    getters: {
        UnansweredQuestions: (state) => (moduleId: string) => {
            return state[moduleId]
                ? state[moduleId].unansweredQuestions
                : [];
        },
        HasNewQuestionsToday: (state) => (moduleId: string) => {
            return state[moduleId]
                ? state[moduleId].unansweredQuestions.some((q: Question) => q.deliveredOn === formatAsIsoDate(DateTime.now()))
                : false;
        },
        GetNextDeliveryDate:(state) => (moduleId: string) => {
            return state[moduleId].nextDeliveryDate
        },
        GetSelectedLanguage:(state) => (moduleId: string) => {
            return state[moduleId].selectedLanguageId;
        },
    }
});

export default useQuestionStore;